<script setup lang="ts">
import { ref, watch, nextTick } from "vue";
import KekkleBottomSheet from "@/components/ui/KekkleBottomSheet.vue";
import KekkleEmptyState from "@/components/ui/KekkleEmptyState.vue";
import Comment from "@/components/Comment.vue";
import CommentInput from "@/components/CommentInput.vue";
import SkeletonLoader from "../ui/SkeletonLoader.vue";
import { useComments } from "@/composables/useComments";
import { useAuthStore } from "@/stores/modules/auth/auth";
import { shortCommentDocumentConverter } from "@kekkle/shared/convertors/web/comment-document.converter";
import { logEvent } from "firebase/analytics";
import { firebaseAnalytics } from "@/helpers/firebase/firebase";

type Props = {
  groupId: string;
  shortId: string;
};

const props = defineProps<Props>();
const isOpen = defineModel<boolean>("open", { required: true });

const authStore = useAuthStore();
const scrollContainer = ref<HTMLDivElement | null>(null);

const {
  comments,
  loading,
  posting,
  postComment: postCommentToFirestore,
} = useComments({
  groupId: props.groupId,
  itemId: props.shortId,
  collectionName: "shorts_comments",
  converter: shortCommentDocumentConverter,
  incrementField: "number_of_comments",
  authorUuid: authStore.authUuid ?? "",
});

function getPreviousDate(index: number): Date | undefined {
  if (index === 0) return undefined;
  return comments.value[index - 1]?.created_at;
}

function scrollToBottom() {
  nextTick(() => {
    if (scrollContainer.value) {
      scrollContainer.value.scrollTop = scrollContainer.value.scrollHeight;
    }
  });
}

watch(
  () => comments.value.length,
  () => {
    scrollToBottom();
  },
  { immediate: true },
);

async function onPostComment(e: { comment: string }) {
  if (!e.comment.trim()) return;
  logEvent(firebaseAnalytics, "added_comment", { for: "short" });
  await postCommentToFirestore(e.comment.trim());
  scrollToBottom();
}
</script>

<template>
  <KekkleBottomSheet
    v-model:open="isOpen"
    shadow="blue"
    padding="normal"
    initial-height="half"
  >
    <div class="flex h-full flex-col">
      <!-- Header -->
      <div
        class="mb-4 flex items-center justify-between pb-2 dark:border-slate-700"
      >
        <h3 class="text-lg font-bold dark:text-slate-100">
          💬 {{ $t("pages.shorts.comments_title") }} ({{ comments.length }})
        </h3>
        <button
          class="text-sm font-semibold text-gray-500 hover:text-gray-800 dark:text-gray-400"
          @click="isOpen = false"
        >
          ✕
        </button>
      </div>

      <!-- Comments List -->
      <div
        ref="scrollContainer"
        class="max-h-96 mb-4 min-h-[160px] flex-1 overflow-y-auto rounded-xl p-2 scrollbar-hide dark:bg-kekkle-dark-light"
      >
        <SkeletonLoader v-if="loading" class="p-8"></SkeletonLoader>
        <KekkleEmptyState
          v-else-if="comments.length === 0"
          emoji="💬"
          :title="$t('pages.shorts.no_comments_yet')"
          class="my-4"
        />
        <comment
          v-for="(item, index) in comments"
          v-else
          :key="`${item.author}-${index}`"
          :author-uuid="item.author"
          :date="item.created_at"
          :previous-date="getPreviousDate(index)"
          :comment="item.comment"
        />
      </div>

      <!-- Comment Input -->
      <div
        class="fixed bottom-0 left-0 right-0 z-10 mx-auto max-w-xl bg-white px-3 pb-4 dark:bg-kekkle-dark"
      >
        <comment-input
          :posting-comment="posting"
          @post-comment="onPostComment"
        />
      </div>
    </div>
  </KekkleBottomSheet>
</template>
